import React from "react";
import { toast } from "../../lib/toast";
import VehicleImage from "../common/VehicleImage";
import { summarizeScheduledBookingAmounts } from "../../lib/scheduledBookingPricing";

const SCHEDULED_BOOKING_NEW_PRICING_MODE = "DISCOUNT_REDUCES_TOTAL";

function VehicleCard({
  brand,
  model,
  type,
  seatingCapacity,
  color,
  fare,
  baseFare,
  availableCount,
  imageUrl,
  modelImage,
  toll,
  commission,
  apiCharges,
  gst,
  discountPercent = 0,
  onAddToCart,
  canAddToCart = true,
  disabledHint,
}) {
  const [adding, setAdding] = React.useState(false);
  const maxQuantity =
    Number.isFinite(Number(availableCount)) && Number(availableCount) > 0
      ? Math.max(1, Math.trunc(Number(availableCount)))
      : 1;
  const [quantity, setQuantity] = React.useState(1);

  React.useEffect(() => {
    setQuantity((prev) => Math.min(Math.max(1, prev), maxQuantity));
  }, [maxQuantity]);

  const safe = (val) => (val === null || val === undefined || val === "" ? "-" : val);
  const seats = Number.isFinite(seatingCapacity) ? seatingCapacity : "-";
  const total = Number(fare ?? baseFare ?? 0);
  const bookingAmount = Math.max(
    0,
    Number(commission ?? 0) + Number(apiCharges ?? 0) + Number(gst ?? 0)
  );
  const totalSelected = total * quantity;
  const bookingSummary = summarizeScheduledBookingAmounts({
    totalFare: totalSelected,
    bookingAmount: Math.ceil(bookingAmount) * quantity,
    discountPercent,
    pricingMode: SCHEDULED_BOOKING_NEW_PRICING_MODE,
  });
  const bookingTotal = bookingSummary.effectiveBookingAmount;

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(Math.max(0, Math.round(value)));

  const formattedFare = formatCurrency(totalSelected);
  const formattedBookingFare = formatCurrency(bookingTotal);
  const formattedOriginalBookingFare = formatCurrency(bookingSummary.originalBookingAmount);
  const formattedPendingFare = formatCurrency(totalSelected - bookingTotal);
  const formattedDiscountAmount = formatCurrency(bookingSummary.discountAmount);
  const rupee = "\u20B9";
  const hasOffer = bookingSummary.discountPercent > 0;
  const offerBadge = bookingSummary.fullyWaived
    ? "BOOK AT 0"
    : hasOffer
    ? `${bookingSummary.discountPercent}% OFF`
    : "BOOKING";
  const offerHeadline = bookingSummary.fullyWaived
    ? `Book your ride by paying ${rupee}0`
    : `Book your ride by paying ${rupee}${formattedBookingFare}`;
  const offerSubline = `Pay after ride ${rupee}${formattedPendingFare}.`;

  const SpecRow = ({ label, children }) => (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2 text-[13px]">
      <span className="text-[11px] uppercase tracking-[0.06em] text-slate-500">{label}</span>
      <span className="whitespace-nowrap font-semibold text-slate-900">{children}</span>
    </div>
  );

  const handleAdd = async () => {
    if (!canAddToCart || !onAddToCart || adding || quantity <= 0) return;

    try {
      setAdding(true);
      await Promise.resolve(onAddToCart(quantity));
      toast.success(
        "Added to cart",
        `${safe(brand)} ${safe(model)} x${quantity} has been added to your cart.`
      );
    } catch (err) {
      toast.error("Couldn't add to cart", (err && err.message) || "Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const buttonDisabled = adding || !canAddToCart || quantity <= 0;
  const finalDisabledHint = !canAddToCart ? disabledHint : undefined;

  return (
    <article
      className="group rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
      aria-label={`${safe(brand)} ${safe(model)} in ${safe(color)}`}
    >
      <div className="flex flex-col lg:flex-row lg:items-stretch">
        <div className="relative min-h-[180px] overflow-hidden rounded-t-2xl bg-white lg:min-h-0 lg:w-[340px] lg:rounded-l-2xl lg:rounded-tr-none">
          <VehicleImage
            imageUrl={imageUrl}
            modelImage={modelImage}
            brand={brand}
            model={model}
            color={color}
            type={type}
            alt={`${safe(brand)} ${safe(model)} ${safe(color)}`}
            wrapperClassName="absolute inset-0 flex items-center justify-start"
            className="h-[92%] w-full scale-[1.01] object-contain object-left"
            allowSkeleton
          />
        </div>

        <div className="flex-1 p-4 md:p-5 lg:pl-4">
          <div className="min-w-0">
            <h2 className="text-[20px] font-semibold leading-tight tracking-tight text-slate-900">
              {safe(brand)} <span className="text-slate-800">{safe(model)}</span>
            </h2>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <SpecRow label="Seats">{seats}</SpecRow>
            <SpecRow label="Color">{safe(color)}</SpecRow>
          </div>

          <div className="mt-3 max-w-[700px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-baseline justify-between gap-3 px-4 pb-2 pt-3">
              <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                Total fare
              </div>
              <div className="text-[20px] font-extrabold tracking-tight text-slate-900 tabular-nums">
                {rupee}
                {formattedFare}
              </div>
            </div>
            <div className="border-t border-slate-100 px-4 py-2.5">
              <div className="text-[14px] font-extrabold text-slate-900">
                {offerHeadline}
              </div>
              <div className="mt-0.5 text-[12px] leading-5 text-slate-600">
                {offerSubline}
              </div>
              <div className={`mt-2.5 grid gap-2.5 ${hasOffer ? "lg:grid-cols-[minmax(0,1.1fr)_minmax(240px,0.9fr)]" : ""}`}>
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    Calculation
                  </div>
                  <div className="mt-1.5 space-y-1 text-[13px] text-slate-700">
                    <div className="flex items-center justify-between gap-3">
                      <span>Per car fare</span>
                      <span className="font-semibold text-slate-900">
                        {rupee}
                        {formatCurrency(total)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>Quantity</span>
                      <span className="font-semibold text-slate-900">
                        {quantity} {quantity === 1 ? "car" : "cars"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-1.5">
                      <span>Total fare</span>
                      <span className="font-semibold text-slate-900">
                        {rupee}
                        {formattedFare}
                      </span>
                    </div>
                  </div>
                </div>
                {hasOffer ? (
                  <div className="rounded-xl border border-rose-100 bg-rose-50/40 px-3 py-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex rounded-full border border-rose-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-rose-700">
                        {offerBadge}
                      </span>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        Booking summary
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div className="rounded-lg border border-white bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">
                          Booking amount
                        </div>
                        <div className="mt-1 text-[15px] font-extrabold text-slate-900">
                          {rupee}
                          {formattedOriginalBookingFare}
                        </div>
                      </div>
                      <div className="rounded-lg border border-white bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">
                          Booking amount discount
                        </div>
                        <div className="mt-1 text-[15px] font-extrabold text-slate-900">
                          -{rupee}
                          {formattedDiscountAmount}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 p-4 lg:flex lg:w-[250px] lg:flex-col lg:justify-between lg:border-l lg:border-t-0 lg:p-5">
          <div className="space-y-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
              <div className="mb-2 text-[12px] text-slate-600">Select quantity</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  disabled={quantity <= 1 || adding}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-lg font-semibold text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                >
                  -
                </button>
                <div className="flex h-9 flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-900">
                  <div className="text-center leading-tight">
                    <div className="text-[10px] uppercase tracking-[0.06em] text-slate-500">
                      Quantity
                    </div>
                    <div className="mt-0.5 text-sm font-semibold text-slate-900">
                      {quantity}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setQuantity((prev) => Math.min(maxQuantity, prev + 1))}
                  disabled={quantity >= maxQuantity || adding}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-lg font-semibold text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 lg:mt-0">
            <button
              type="button"
              onClick={!buttonDisabled ? handleAdd : undefined}
              disabled={buttonDisabled}
              aria-busy={adding}
              className={`inline-flex h-11 w-full items-center justify-center rounded-md px-5 text-sm font-semibold transition ${
                buttonDisabled
                  ? "cursor-not-allowed bg-slate-200 text-slate-500"
                  : "bg-black text-white hover:opacity-90 active:opacity-80"
              }`}
            >
              {adding ? "Adding..." : "Add to cart"}
            </button>

            {buttonDisabled && finalDisabledHint ? (
              <div className="pt-1 text-xs text-slate-500">{finalDisabledHint}</div>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

export default React.memo(VehicleCard);
